import { captureException } from "src/lib/monitoring";
import isEqual from "lodash/isEqual";
import pick from "lodash/pick";
import { isPresent } from "ts-extras";
import { v4 as uuidv4 } from "uuid";

import { getModelIdFromColumn } from "src/components/explore/visual/utils";
import {
  type AudienceSplitInformation,
  type ChartFormState,
  ChartTab,
  type ConversionWindow,
  FunnelMeasurementType,
  type FunnelStep,
  isMetricSelection,
  type MeasurementGroupOption,
  MeasurementScope,
  type MeasuringSelection,
  type Metric,
  type MetricSelection,
  type ParentModel,
  type SelectedAudience,
  type SelectedAudienceWithBaseline,
  type TimeOptions,
} from "src/pages/analytics/types";
import {
  getEventIdFromMetricSelection,
  getMetricSelectionFromMetric,
  getTimeValueAndSelectedDates,
  mapMetricToMetricSelection,
  transformSavedChartGraphType,
  transformSavedChartTab,
} from "src/pages/analytics/utils";
import {
  transformSavedChartFrequency,
  type MeasuringMode,
  type GroupByColumn,
  type CohortFilter,
} from "@hightouch/lib/query/visual/types";
import type {
  AttributionWindow,
  AudienceAggregationType,
  NormalizationType,
  PerUserAggregationType,
  Relationship,
} from "src/types/visual";

import type { AudiencesAndMetricsForParentModelQuery } from "src/graphql";
import {
  flowSyntheticColumnValuesGetter,
  type MinimalDecisionEngineFlowMessage,
} from "src/pages/analytics/decision-engine-utils";
import { convertSavedCohortsToSelectedAudiences } from "src/pages/analytics/utils/convert-saved-cohorts-to-selected-audiences";
import {
  DEFAULT_EXPERIMENT_MEASUREMENT_WINDOW,
  DEFAULT_FUNNEL_CONVERSION_WINDOW,
  DEFAULT_GROUP_BY_COLUMNS,
  getDefaultFunnelSteps,
  NULL_OPTION_VALUE,
} from "./constants";

export type SavedCohortDefinition = {
  id: string;
  cohort_definition: any | null;
  cohort: {
    id: string;
    name: string;
  } | null;
};

type GetChartStateFromChartDefinition = {
  name: string;
  description: string | null;
  frequency: string;
  chart_type: string;
  conversion_window: ConversionWindow | null;
  measuring_id: string | null;
  measuring_mode: string | null;
  cumulative: boolean | null;
  lookback_window:
    | {
        start: string;
        end: string;
      }
    | {
        lookback: TimeOptions | number;
      }
    | undefined;
  group_by: (GroupByColumn | undefined)[];
  parent_model_id: number;
  cohort_filter: CohortFilter | null;
  measurement_window: AttributionWindow | null;
  normalization: string | null;
  goals: {
    id: string;
    goal_definition: any | null;
    goal: {
      id: string;
      parent_model_id: string;
      name: string;
    } | null;
  }[];
  cohorts: SavedCohortDefinition[];
  funnel_stages: {
    id: string;
    funnel_stage_definition: any | null;
    relationship_id: any | null;
    event_model: {
      id: any;
      name: string;
    } | null;
  }[];
};

type ChartDependencies = {
  parentModel: ParentModel | null;
  metricOptions: Metric[];
  audienceOptions: SelectedAudience[];
  events: Relationship[];
  measurementOptions: MeasurementGroupOption[];
  measurementOptionsLoading?: boolean;
};

export const getChartStateFromChartDefinition = (
  chart: GetChartStateFromChartDefinition | null | undefined,
  dependencies: ChartDependencies,
): ChartFormState | undefined => {
  if (chart) {
    const {
      name,
      description,
      frequency,
      chart_type,
      lookback_window,
      group_by,
      parent_model_id,
      goals,
      cohorts,
      funnel_stages,
      conversion_window,
      cumulative,
      measuring_id,
      measuring_mode,
      cohort_filter,
      measurement_window,
      normalization,
    } = chart;
    const metricSelection: MetricSelection[] = [];

    goals?.forEach(({ goal, goal_definition }) => {
      if (goal) {
        const data = getMetricSelectionFromMetric({
          metricId: goal.id,
          events: dependencies.events,
          metrics: dependencies.metricOptions,
        });

        if (data) {
          metricSelection.push(data);
        }
      } else if (goal_definition) {
        let goalDefinition: MetricSelection | null = goal_definition;
        if (!goal_definition?.aggregationMethod) {
          goalDefinition = mapMetricToMetricSelection(
            goal_definition as Metric,
            dependencies.metricOptions,
            dependencies.parentModel?.filterable_audience_columns ?? [],
            dependencies.events,
          );
        }

        if (!goalDefinition || !isMetricSelection(goalDefinition)) {
          captureException(
            "Saved goal definition is not of type MetricSelection",
          );
        }

        if (goalDefinition) {
          metricSelection.push(goalDefinition);
        }
      }
    });

    const selectedAudiences: SelectedAudience[] =
      convertSavedCohortsToSelectedAudiences({
        cohorts,
        audienceOptions: dependencies.audienceOptions,
        parentModel: dependencies.parentModel ?? {
          id: parent_model_id,
          name: "",
        },
      });

    const { timeValue, selectedDates } =
      getTimeValueAndSelectedDates(lookback_window);

    const funnelSteps: FunnelStep[] = (funnel_stages ?? [])
      .map((stage) => {
        if (!stage.event_model || !stage.relationship_id) {
          captureException(
            `Funnel stage ${stage.id}: event or relationship was deleted.`,
          );
          return null;
        }

        return {
          id: uuidv4(),
          eventModelId: stage.event_model.id,
          relationshipId: stage.relationship_id,
          subconditions: stage.funnel_stage_definition?.subconditions ?? [],
        };
      })
      .filter(isPresent);

    const graphType = transformSavedChartGraphType(chart_type);

    // Default should be the all data option
    let measuringSelection: MeasuringSelection | undefined = {
      id: NULL_OPTION_VALUE,
      scope: MeasurementScope.AllData,
    };

    if (measuring_id && !dependencies.measurementOptionsLoading) {
      const measurementsOptions = dependencies.measurementOptions.flatMap(
        ({ options }) => options,
      );

      const foundMeasuringSelection = measurementsOptions.find(
        (m) => m.id === measuring_id,
      );

      if (foundMeasuringSelection) {
        measuringSelection = {
          id: measuring_id,
          scope: foundMeasuringSelection.scope,
        };
      } else {
        measuringSelection = undefined;
      }
    }

    const result: ChartFormState = {
      parentModelId: parent_model_id,
      name,
      description,
      chartTab: transformSavedChartTab(chart_type),
      graphType,
      groupByColumns: group_by ?? [],
      metricSelection,
      measuringSelection,
      measuringMode: measuring_mode as MeasuringMode,
      timeValue,
      funnelSteps:
        funnelSteps.length > 0 ? funnelSteps : getDefaultFunnelSteps(),
      selectedDates,
      rollupFrequency: transformSavedChartFrequency(frequency, graphType),
      selectedAudiences,
      funnelConversionWindow:
        conversion_window ?? DEFAULT_FUNNEL_CONVERSION_WINDOW,
      cumulative: cumulative ?? false,
      funnelMeasuringType: FunnelMeasurementType.TotalConversion,
      globalCohortFilter: cohort_filter ?? undefined,
      globalMeasurementWindow:
        measurement_window ?? DEFAULT_EXPERIMENT_MEASUREMENT_WINDOW,
      globalNormalization: (normalization as NormalizationType) ?? undefined,
    };

    // If we're in splits measurement mode, we need to format the cohorts
    // to be in the new cohort format
    if (chart.chart_type === ChartTab.SplitsMeasurement) {
      const formattedCohorts: SelectedAudienceWithBaseline[] =
        selectedAudiences.map((audience) => ({
          cohort: audience,
          baselineGroup:
            audience.splits.find((split) => split.isHoldoutGroup)?.name ??
            audience.splits[0]?.name,
        }));

      result.cohorts = formattedCohorts;
      result.selectedAudiences = undefined;
    }

    return result;
  }

  return undefined;
};

/**
 * Filters out any group by columns that are not related to the parent model or the selected metrics/events
 */
export const getFilteredGroupByColumnsFromMetricSelection = ({
  parentModelId,
  selectedMetrics,
  metricOptions,
  selectedGroupByColumns,
}: {
  parentModelId: number | null;
  selectedMetrics: MetricSelection[];
  metricOptions: Metric[];
  selectedGroupByColumns: GroupByColumn[];
}) => {
  const modelIds = selectedMetrics
    .map((updatedMetric) =>
      getEventIdFromMetricSelection(updatedMetric, metricOptions)?.toString(),
    )
    .concat([parentModelId?.toString()])
    .filter(isPresent);

  const filteredGroupByColumns = selectedGroupByColumns.filter((column) => {
    const modelId = getModelIdFromColumn(column);
    return modelId && modelIds.includes(modelId);
  });

  return filteredGroupByColumns.length > 0
    ? filteredGroupByColumns
    : DEFAULT_GROUP_BY_COLUMNS;
};

/**
 * If available, grab a function to fetch values for a given synthetic column
 */
export const getSyntheticColumnValuesGetter = (
  measuringSelection: MeasuringSelection | undefined,
  measuringMetricResources: Record<string, unknown>,
) => {
  switch (measuringSelection?.scope) {
    case MeasurementScope.DecisionEngineFlow:
      return flowSyntheticColumnValuesGetter(
        measuringMetricResources.flowMessages as MinimalDecisionEngineFlowMessage[],
      );
    case MeasurementScope.AllData:
    default:
      return undefined;
  }
};

export const transformAudiencesAndMetrics = (
  data: AudiencesAndMetricsForParentModelQuery,
) => {
  const { segments, goals } = data;

  return {
    ...data,
    goals: goals.map(({ audience_aggregation, ...goal }) => ({
      ...goal,
      aggregation: goal.aggregation as PerUserAggregationType,
      audienceAggregation: audience_aggregation as AudienceAggregationType,
    })),
    segments: segments.map((segment) => ({
      ...segment,
      splits: segment.splits.map(formatSplit),
    })),
  };
};

type Split = {
  id: string;
  percentage: number;
  friendly_name: string;
  destination_instance_splits: {
    sync: {
      destination: {
        definition: {
          icon: string;
          name: string;
        };
      } | null;
      sync_requests: {
        finished_at: string | null;
      }[];
    } | null;
  }[];
  is_holdout_group: boolean;
};

export const formatSplit = (split: Split): AudienceSplitInformation => {
  const {
    friendly_name,
    destination_instance_splits,
    is_holdout_group,
    ...rest
  } = split;

  const firstSyncAt = destination_instance_splits
    ?.flatMap(({ sync }) => sync?.sync_requests?.[0] ?? null)
    .filter(isPresent)
    .sort((a, b) => {
      const aFinishedAt = a.finished_at;
      const bFinishedAt = b.finished_at;
      if (!aFinishedAt) return 1;
      if (!bFinishedAt) return -1;
      return new Date(aFinishedAt).getTime() - new Date(bFinishedAt).getTime();
    })?.[0]?.finished_at;

  const destinations = destination_instance_splits
    .flatMap(({ sync }) => sync)
    .filter(isPresent)
    .map((sync) => {
      return {
        icon: sync.destination?.definition?.icon,
        name: sync.destination?.definition?.name,
      };
    })
    .filter(isPresent);

  return {
    ...rest,
    name: friendly_name,
    enabled: true,
    isHoldoutGroup: is_holdout_group,
    firstSyncAt: firstSyncAt ?? undefined,
    destinations,
  };
};

const QUERY_RELEVANT_KEYS: (keyof ChartFormState)[] = [
  "parentModelId",
  "chartTab",
  "metricSelection",
  "selectedAudiences",
  "cohorts",
  "groupByColumns",
  "timeValue",
  "selectedDates",
  "rollupFrequency",
  "graphType",
  "measuringSelection",
  "measuringMode",
  "cumulative",
  "funnelSteps",
  "funnelConversionWindow",
  "globalCohortFilter",
  "globalMeasurementWindow",
  "globalNormalization",
];

export const getQueryRelevantFormValues = (
  formState: ChartFormState,
): Partial<ChartFormState> => pick(formState, QUERY_RELEVANT_KEYS);

export const isQueryStale = (
  currentValues: ChartFormState,
  lastQueriedValues: Partial<ChartFormState> | null,
): boolean => {
  return !isEqual(getQueryRelevantFormValues(currentValues), lastQueriedValues);
};
